import { useCallback, useState, forwardRef } from 'react';
import { flushSync } from 'react-dom';
import * as Popover from '@radix-ui/react-popover';
import { Widget } from '@/modules';
import { useWidgetGlobalOperations } from '@/modules/global/hooks/useWidgetGlobalOperations';
import { Tooltip } from '@/components/molecules/Tooltip';
import { MinimizeIcon, MaximizeIcon, TrashIcon, EditIcon, ThreeDotsIcon } from '@/components/icons';
import { Button } from '@/components/atoms';
import { FileDown, Copy, Share2 } from 'lucide-react';
import { LoadingSpinnerIcon } from '@/components/icons/LoadingSpinnerIcon';

interface WidgetActionsProps {
  widget: Widget;
  widgetData?: any;
  showDeleteButton?: boolean;
  showExportButton?: boolean;
  onExportClick?: (widget: Widget) => void;
  isExportDisabled?: boolean;
  isExportLoading?: boolean;
  onDelete?: () => void;
  onEditClick?: () => void;
  showEditButton?: boolean;
  showDuplicateButton?: boolean;
  onDuplicateClick?: () => void;
  showShareButton?: boolean;
  onShareClick?: () => void;
  shareDisabledReason?: string;
  isMinimized?: boolean;
  onMinimizeToggle?: () => void;
}

export const WidgetActions = forwardRef<HTMLDivElement, WidgetActionsProps>(({
  widget,
  showDeleteButton = false,
  onDelete,
  showExportButton = false,
  onExportClick,
  isExportLoading,
  isExportDisabled = false,
  onEditClick,
  showEditButton = false,
  showDuplicateButton = false,
  onDuplicateClick,
  showShareButton = false,
  onShareClick,
  shareDisabledReason,
  isMinimized = false,
  onMinimizeToggle
}, ref) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteWidget } = useWidgetGlobalOperations();

  const handleDelete = async () => {
    try {
      flushSync(() => {
        setIsDeleting(true);
      });
      await deleteWidget(widget);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting widget:', error);
    } finally {
      setIsDeleting(false);
    }
  }

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick();
    }
  }, [onEditClick]);

  const handleDuplicateClick = useCallback(() => {
    if (onDuplicateClick) {
      onDuplicateClick();
    }
  }, [onDuplicateClick]);

  const handleExportClick = useCallback(() => {
    if (onExportClick) {
      onExportClick(widget);
    }
  }, [onExportClick]);

  const hasMenuItems = showEditButton || showDuplicateButton || showDeleteButton || showExportButton || showShareButton;

  return (
    <div ref={ref} className="relative flex items-center gap-2" id="widget-actions">

      {hasMenuItems && (
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              className="p-1 rounded-full hover:bg-gray-100 transition-all duration-200 text-gray-500 hover:text-gray-700 cursor-pointer"
              aria-label="Widget actions"
            >
              <ThreeDotsIcon className="w-4 h-4" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="end"
              sideOffset={4}
              className="z-50 bg-white shadow-lg rounded-md py-1 border border-gray-200 min-w-[140px]"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {showEditButton && (
                <Popover.Close asChild>
                  <button
                    onClick={handleEditClick}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto"
                    >
                    <EditIcon className="w-4 h-4" />
                    Edit
                  </button>
                </Popover.Close>
              )}
              {showExportButton && (
                <Popover.Close asChild>
                    <button
                      onClick={handleExportClick}
                      disabled={isExportLoading || isExportDisabled}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto disabled:opacity-60"
                    >
                      <FileDown className="w-4 h-4" color="#8FB4C2" />
                      Export
                    </button>
                  </Popover.Close>
              )}
              {showDuplicateButton && (
                <Popover.Close asChild>
                  <button
                    onClick={handleDuplicateClick}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto"
                  >
                    <Copy className="w-4 h-4" color="#8FB4C2" />
                    Duplicate
                  </button>
                </Popover.Close>
              )}
              {showShareButton && (
                shareDisabledReason ? (
                  <Tooltip content={shareDisabledReason} position="left">
                    <button
                      disabled
                      className="w-full px-3 py-2 text-left text-sm text-gray-400 flex items-center gap-2 font-roboto cursor-not-allowed"
                    >
                      <Share2 className="w-4 h-4" color="#d1d5db" />
                      Share
                    </button>
                  </Tooltip>
                ) : (
                  <Popover.Close asChild>
                    <button
                      onClick={onShareClick}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto"
                    >
                      <Share2 className="w-4 h-4" color="#8FB4C2" />
                      Share
                    </button>
                  </Popover.Close>
                )
              )}
              {showDeleteButton && !showDeleteConfirm && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-roboto"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              )}
              {showDeleteButton && showDeleteConfirm && (
                <div className="px-3 py-2">
                  <p className="text-sm text-gray-700 mb-2 font-roboto">Delete this widget?</p>
                  <div className="flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition-colors font-roboto"
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <Button
                      isLoading={isDeleting}
                      type="button"
                      onClick={handleDelete}
                      className="text-xs px-3 py-1.5 bg-[#E15E64] hover:bg-white hover:text-[#2D3A3F] hover:border-[#D3DFE8] border border-transparent rounded text-white transition-colors font-roboto"
                      disabled={isDeleting}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}

      {onMinimizeToggle && (
        <Tooltip className='inline-flex' content={isMinimized ? "Maximize" : "Minimize"}>
          <button
            onClick={onMinimizeToggle}
            className="p-0.5 rounded-full hover:bg-gray-100 transition-all duration-200 text-gray-500 hover:text-gray-700 cursor-pointer"
            aria-label={isMinimized ? "Maximize widget" : "Minimize widget"}
            type="button"
          >
            {isMinimized ? <MaximizeIcon className="w-4 h-4" /> : <MinimizeIcon className="w-4 h-4" />}
          </button>
        </Tooltip>
      )}
    </div>
  );
});

WidgetActions.displayName = 'WidgetActions';
